export type RecordingFormat = '16:9' | '9:16' | '4:5' | '1:1';

export interface OutputSize {
  width: number;
  height: number;
}

const OUTPUT_SIZES: Record<RecordingFormat, OutputSize> = {
  '16:9': { width: 1920, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
  '4:5':  { width: 1080, height: 1350 },
  '1:1':  { width: 1080, height: 1080 },
};

// MP4/H.264 preferred for broad compatibility and to avoid the WebM "Infinity
// duration" bug that makes some players miscalculate playback speed.
//
// The bare 'video/mp4' MIME comes first deliberately: the browser then picks
// an appropriate H.264 profile/level for the actual content dimensions, and
// (critically) honors videoBitsPerSecond — Chrome's H.264 encoder respects
// the bitrate target within ~10–15% when the codec config doesn't conflict
// with the content. Pinning a specific level (e.g. avc1.42E01E = Baseline
// Level 3.0, which formally caps at 720×576) on 1080p content was causing
// the encoder to silently override the level AND ignore the bitrate target,
// producing files at ~3–4× the requested rate.
//
// 'avc1.640028' = High Profile, Level 4.0 — supports up to 1080p/60 at
// 20 Mbps, plenty of headroom for our target. Kept as a specific fallback
// behind the bare MIME so any UA defaulting to a weird codec doesn't ignore
// our bitrate.
//
// Audio codecs intentionally omitted — audio capture is disabled. Re-add
// `mp4a.40.2` (mp4) or `opus` (webm) when audio comes back.
const MIME_PRIORITY: ReadonlyArray<string> = [
  'video/mp4',
  'video/mp4;codecs=avc1.640028',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
];

type CapturableVideoTrack = MediaStreamTrack & {
  requestFrame?: () => void;
};

declare const MediaStreamTrackGenerator: {
  new (init: { kind: 'video' | 'audio' }): MediaStreamTrack & { writable: WritableStream<VideoFrame> };
};

/**
 * Paints one output frame. Return false to skip writing this frame entirely
 * (e.g. the source has not produced anything yet); return true once the canvas
 * holds a frame worth encoding.
 */
export type FrameDrawFn = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
) => boolean;

export function pickMimeType(): string {
  for (const mime of MIME_PRIORITY) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  return 'video/webm';
}

/**
 * Chrome's MediaRecorder H.264 encoder treats videoBitsPerSecond as a very
 * soft hint when fed VideoFrames via MediaStreamTrackGenerator — empirically
 * the actual encoded file lands around 3-4× the requested target (e.g. an
 * 8 Mbps hint produced ~27 Mbps in our testing). The reference below is
 * pre-compensated for that multiplier so the actual encoded bitrate lands
 * near the user-visible target of ~8 Mbps for 1080p, scaled linearly by pixel
 * count for other aspect ratios:
 *
 *   target user-visible ≈ requested-reference × 3.4 (empirical)
 *   target user-visible (1080p) = 2.5 Mbps × 3.4 ≈ 8.5 Mbps
 *
 * Per-format reference bitrates after pixel scaling:
 *   16:9  1920×1080 = 2.07 Mpx → ref 2.50 Mbps → expected ~8.5 Mbps file
 *   9:16  1080×1920 = 2.07 Mpx → ref 2.50 Mbps → expected ~8.5 Mbps file
 *   4:5   1080×1350 = 1.46 Mpx → ref 1.76 Mbps → expected ~6.0 Mbps file
 *   1:1   1080×1080 = 1.17 Mpx → ref 1.41 Mbps → expected ~4.8 Mbps file
 *
 * If Chrome ever fixes its rate control behavior, lift this reference back to
 * 8 Mbps. If actual files still drift significantly above the expected
 * values, the next step is to bypass MediaRecorder entirely and use WebCodecs
 * VideoEncoder + mp4-muxer for strict bitrate control.
 */
export function computeVideoBitsPerSecond(size: OutputSize): number {
  const referenceBitrate = 2_500_000;
  const referencePixels = 1920 * 1080;
  return Math.round(referenceBitrate * (size.width * size.height) / referencePixels);
}

function timestamp(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

export function downloadRecording(
  blob: Blob,
  mimeType: string,
  format: RecordingFormat,
  prefix: string,
): void {
  const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
  const stamp = timestamp();
  const safeFormat = format.replace(':', 'x');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${prefix}-${safeFormat}-${stamp}.${ext}`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Source-agnostic output half of a recording pipeline: owns the composite
 * canvas, the output MediaStreamTrack, the MediaRecorder and teardown.
 *
 * Callers drive it by calling captureFrame(draw) at their target rate; the
 * draw callback paints the composite canvas from whatever source the caller
 * has. Frames are written with wall-clock-anchored timestamps so the output
 * timeline stays honest even when the driver fires irregularly.
 */
export class FrameSink {
  private compositeCanvas: HTMLCanvasElement | null = null;
  private compositeCtx: CanvasRenderingContext2D | null = null;
  private outputSize: OutputSize = { width: 0, height: 0 };
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private trackWriter: WritableStreamDefaultWriter<VideoFrame> | null = null;
  private useTrackGenerator = false;
  // Fallback path when MediaStreamTrackGenerator is unavailable: capture from
  // the 2D composite canvas via captureStream(0) + requestFrame().
  private fallbackVideoTrack: CapturableVideoTrack | null = null;
  private targetFrameDurationUs = 0;
  // Monotonic frame counter for instrumentation/diagnostics.
  private frameCounter = 0;
  // Wall-clock time (performance.now() in ms) of the first output frame —
  // every subsequent frame's timestamp is measured relative to this. Using
  // wall-clock instead of counter-based timestamps keeps the video timeline
  // aligned with the audio track (which is itself wall-clock based), so any
  // jitter or under-target-FPS on the output ticker does not cause audio drift.
  private recordingStartMs = 0;
  // Latches so a per-frame failure is reported once, not thousands of times.
  private reportedWriteError = false;

  start(format: RecordingFormat, fps: number): void {
    if (this.recorder) {
      throw new Error('FrameSink.start called while already recording');
    }

    const safeFps = Number.isFinite(fps) && fps > 0 ? Math.round(fps) : 30;
    const size = OUTPUT_SIZES[format];
    this.outputSize = size;

    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Failed to acquire 2D context for composite canvas');
    this.compositeCanvas = canvas;
    this.compositeCtx = ctx;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, size.width, size.height);

    this.targetFrameDurationUs = Math.round(1_000_000 / safeFps);
    this.frameCounter = 0;
    this.recordingStartMs = 0;
    this.reportedWriteError = false;

    const stream = new MediaStream();
    const hasGenerator = typeof (globalThis as any).MediaStreamTrackGenerator === 'function';
    const hasVideoFrame = typeof (globalThis as any).VideoFrame === 'function';

    if (hasGenerator && hasVideoFrame) {
      const generator = new MediaStreamTrackGenerator({ kind: 'video' });
      this.trackWriter = generator.writable.getWriter();
      stream.addTrack(generator);
      this.useTrackGenerator = true;
    } else {
      const videoStream = canvas.captureStream(0);
      videoStream.getVideoTracks().forEach((t) => stream.addTrack(t));
      this.fallbackVideoTrack = (videoStream.getVideoTracks()[0] ?? null) as CapturableVideoTrack | null;
      this.useTrackGenerator = false;
    }

    // Audio capture intentionally disabled for now — the audio track from
    // <video>.captureStream() does not stay aligned with our wall-clock-stamped
    // video frames, producing audible drift in the output. Re-enable once we
    // have a sync strategy that holds across paused intervals.
    this.stream = stream;

    this.recorder = new MediaRecorder(stream, {
      mimeType: pickMimeType(),
      videoBitsPerSecond: computeVideoBitsPerSecond(size),
    });
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start(1000);
  }

  /**
   * Cadence signal from the driver. Runs `draw` against the composite canvas
   * and, unless it declines, emits exactly one output frame.
   */
  captureFrame(draw: FrameDrawFn): void {
    if (!this.recorder || this.recorder.state !== 'recording') return;
    const ctx = this.compositeCtx;
    const canvas = this.compositeCanvas;
    if (!ctx || !canvas) return;
    const { width, height } = this.outputSize;

    if (!draw(ctx, width, height)) return;

    if (this.useTrackGenerator && this.trackWriter) {
      try {
        const nowMs = performance.now();
        if (this.recordingStartMs === 0) {
          this.recordingStartMs = nowMs;
        }
        // Wall-clock timestamp in microseconds, anchored at the first written
        // frame. Audio samples from captureStream() are timestamped on the
        // same wall-clock — sharing the clock keeps the two tracks aligned
        // regardless of how often the output ticker actually fires.
        const ts = Math.round((nowMs - this.recordingStartMs) * 1000);
        const outFrame = new VideoFrame(canvas, {
          timestamp: ts,
          duration: this.targetFrameDurationUs,
        });
        this.trackWriter.write(outFrame).catch(() => { /* closed during teardown */ });
        this.frameCounter++;
      } catch (err) {
        // Report the first failure only — this fires per frame, and silently
        // skipping every one produces an empty recording with no clue why.
        // The usual cause is a tainted canvas: a cross-origin frame source
        // drawn without CORS makes VideoFrame construction throw SecurityError.
        if (!this.reportedWriteError) {
          this.reportedWriteError = true;
          console.error('Recording: failed to emit a frame; output will be empty', err);
        }
      }
      return;
    }

    if (this.fallbackVideoTrack?.requestFrame) {
      try { this.fallbackVideoTrack.requestFrame(); } catch { /* ignore */ }
      this.frameCounter++;
    }
  }

  async stop(): Promise<{ blob: Blob; mimeType: string } | null> {
    if (!this.recorder) return null;
    const recorder = this.recorder;
    const finishedMime = recorder.mimeType || 'video/webm';

    const done = new Promise<void>((resolve) => {
      recorder.addEventListener('stop', () => resolve(), { once: true });
    });
    if (recorder.state !== 'inactive') {
      recorder.stop();
    }
    await done;

    this.release();

    const blob = this.chunks.length > 0
      ? new Blob(this.chunks, { type: finishedMime })
      : null;
    this.chunks = [];
    this.recorder = null;
    this.compositeCanvas = null;
    this.compositeCtx = null;

    return blob ? { blob, mimeType: finishedMime } : null;
  }

  isRecording(): boolean {
    return this.recorder?.state === 'recording';
  }

  private release(): void {
    if (this.trackWriter) {
      try { this.trackWriter.close().catch(() => {}); } catch { /* ignore */ }
      this.trackWriter = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.fallbackVideoTrack = null;
    this.useTrackGenerator = false;
  }
}

export { OUTPUT_SIZES };
